export interface WebAuthConfig {
  tenantId: string;
  tenantSubdomain: string;
  authorityDomain: string;
  clientId: string;
  redirectUri: string;
  postLogoutRedirectUri: string;
  apiScope: string;
}

interface AuthEnvironment {
  readonly PROD: boolean;
  readonly VITE_AUTH_TENANT_ID?: string;
  readonly VITE_AUTH_TENANT_SUBDOMAIN?: string;
  readonly VITE_AUTH_AUTHORITY_DOMAIN?: string;
  readonly VITE_AUTH_CLIENT_ID?: string;
  readonly VITE_AUTH_API_SCOPE?: string;
}

const localDevelopmentDefaults = {
  tenantId: 'baf8c799-55bb-481d-ba6b-ff989086852b',
  tenantSubdomain: 'dgmvp',
  authorityDomain: 'dgmvp.ciamlogin.com',
  clientId: '331f3482-2305-4a28-abbb-a719a7905ff3',
  apiScope: 'api://dgmvp/access_as_user',
} as const;

function normalizeConfiguredValue(value: string | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function resolveRequiredAuthValue(
  env: AuthEnvironment,
  key: keyof Omit<AuthEnvironment, 'PROD'>,
  fallback: string,
): string {
  const configured = normalizeConfiguredValue(env[key]);
  if (configured) {
    return configured;
  }

  if (!env.PROD) {
    return fallback;
  }

  throw new Error(`Missing required auth build variable ${key}.`);
}

function normalizeAppOrigin(appOrigin: string): string {
  return appOrigin.endsWith('/') ? appOrigin.slice(0, -1) : appOrigin;
}

export function resolveAuthConfig(env: AuthEnvironment, appOrigin: string): WebAuthConfig {
  const normalizedAppOrigin = normalizeAppOrigin(appOrigin);

  return {
    tenantId: resolveRequiredAuthValue(env, 'VITE_AUTH_TENANT_ID', localDevelopmentDefaults.tenantId),
    tenantSubdomain: resolveRequiredAuthValue(
      env,
      'VITE_AUTH_TENANT_SUBDOMAIN',
      localDevelopmentDefaults.tenantSubdomain,
    ),
    authorityDomain: resolveRequiredAuthValue(
      env,
      'VITE_AUTH_AUTHORITY_DOMAIN',
      localDevelopmentDefaults.authorityDomain,
    ),
    clientId: resolveRequiredAuthValue(env, 'VITE_AUTH_CLIENT_ID', localDevelopmentDefaults.clientId),
    redirectUri: `${normalizedAppOrigin}/auth/callback`,
    postLogoutRedirectUri: `${normalizedAppOrigin}/login`,
    apiScope: resolveRequiredAuthValue(env, 'VITE_AUTH_API_SCOPE', localDevelopmentDefaults.apiScope),
  };
}